import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Blockad = {
    id: number;
    code: string;
    displayName: string;
    ordning: number;
};

export type BlockadDisplay = {
    value: Blockad["id"];
    label: string;
};

type State = BaseState & {
    blockad: Map<Blockad["id"], Blockad>;
};

export const convertBlockadData: (args: { blockad?: Blockad }) => BlockadDisplay | null = ({ blockad }) => {
    if (!blockad) {
        return null;
    }

    return {
        value: blockad.id,
        label: blockad.displayName,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        blockad: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "blockad";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        blockad: (state: State) => [...state.blockad.values()],
        blockadSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertBlockadData({ blockad: singleResult }));
            }

            return result;
        },
        blockadById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.blockad.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addBlockad: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                blockad = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                blockad: Blockad[];
            }
        ) =>
            subsetSetter<State, Blockad>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: blockad,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchBlockad: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Blockad>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "operation/kodverk/blockader/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addBlockad", {
                    subsetName,
                    offset: resultOffset,
                    blockad: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertBlockadData({ blockad: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
